import React, { useCallback, useEffect, useId, useRef, useState } from 'react';
import { Modal, Theme } from '@mui/material';
import { makeStyles } from '@mui/styles';
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';

import { CS_FONT, CS_WEIGHT, csTokens } from 'components/CallScribe/callScribeTheme';

export const EXIT_MS = 120;

const prefersReducedMotion = (): boolean =>
  typeof window.matchMedia === 'function' && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

const useSheetStyles = makeStyles((theme: Theme) => {
  const t = csTokens(theme);
  return {
    sheet: {
      position: 'fixed',
      inset: 0,
      isolation: 'isolate',
      display: 'flex',
      flexDirection: 'column',
      background: t.bg,
      color: t.text,
      outline: 'none',
      fontFamily: "'Nunito', sans-serif",
      animation: '$sheetIn 200ms ease-out',
      '@media (prefers-reduced-motion: reduce)': { animation: '$fadeIn 150ms linear' },
    },
    closing: {
      pointerEvents: 'none',
      animation: `$sheetOut ${EXIT_MS}ms ease-in forwards`,
      '@media (prefers-reduced-motion: reduce)': { animation: 'none' },
    },
    '@keyframes sheetOut': {
      from: { opacity: 1, transform: 'none' },
      to: { opacity: 0, transform: 'translateY(8px)' },
    },
    '@keyframes sheetIn': {
      from: { opacity: 0, transform: 'translateY(8px)' },
      to: { opacity: 1, transform: 'none' },
    },
    '@keyframes fadeIn': {
      from: { opacity: 0 },
      to: { opacity: 1 },
    },
    header: {
      flex: '0 0 auto',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      gap: 8,
      padding: '12px 8px 4px 16px',
    },
    title: {
      ...CS_FONT.title,
      fontWeight: CS_WEIGHT.heavy,
      margin: 0,
      color: t.text,
    },
    close: {
      width: 44,
      height: 44,
      flex: '0 0 auto',
      display: 'inline-grid',
      placeItems: 'center',
      border: 'none',
      borderRadius: 12,
      background: 'transparent',
      color: t.text2,
      cursor: 'pointer',
      transition: 'background-color 150ms ease-out',
      '&:hover': { background: t.surface3, color: t.text },
      '&:focus-visible': { outline: `2px solid ${t.focus}`, outlineOffset: 2 },
    },
    body: {
      flex: '1 1 auto',
      minHeight: 0,
      overflowY: 'auto',
      display: 'flex',
      flexDirection: 'column',
    },
  };
});

export interface ISheetProps {
  open: boolean;
  title: string;
  onClose: () => void;
  initialFocusRef?: React.RefObject<HTMLElement | null>;
  onHeightChange?: (height: number) => void;
  footer?: React.ReactNode;
  children: React.ReactNode;
}

const Sheet: React.FC<ISheetProps> = ({
  open,
  title,
  onClose,
  initialFocusRef,
  onHeightChange,
  footer,
  children,
}) => {
  const classes = useSheetStyles();
  const titleId = useId();
  const [shown, setShown] = useState(open);
  const observer = useRef<ResizeObserver | null>(null);
  const heightCallback = useRef(onHeightChange);
  heightCallback.current = onHeightChange;

  if (open && !shown) setShown(true);
  const closing = !open && shown;

  const ignoreWhileClosing = (e: React.SyntheticEvent): void => {
    if (!closing) return;
    e.preventDefault();
    e.stopPropagation();
  };

  useEffect(() => {
    if (open || !shown) return undefined;
    if (prefersReducedMotion()) {
      setShown(false);
      return undefined;
    }
    const timer = window.setTimeout(() => { setShown(false); }, EXIT_MS);
    return () => { window.clearTimeout(timer); };
  }, [open, shown]);

  const attach = useCallback((node: HTMLDivElement | null) => {
    observer.current?.disconnect();
    observer.current = null;
    if (!node) return;
    initialFocusRef?.current?.focus();
    heightCallback.current?.(node.getBoundingClientRect().height);
    if (typeof ResizeObserver === 'undefined') return;
    observer.current = new ResizeObserver(entries => {
      const entry = entries[entries.length - 1];
      if (entry) heightCallback.current?.(entry.contentRect.height);
    });
    observer.current.observe(node);
  }, [initialFocusRef]);

  return (
    <Modal open={shown} onClose={closing ? undefined : onClose} hideBackdrop keepMounted={false}>
      <div
        ref={attach}
        data-state={closing ? 'closing' : 'open'}
        className={`${classes.sheet} ${closing ? classes.closing : ''}`}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        tabIndex={-1}
        onClickCapture={ignoreWhileClosing}
        onKeyDownCapture={ignoreWhileClosing}
      >
        <div className={classes.header}>
          <h2 id={titleId} className={classes.title}>{title}</h2>
          <button type="button" className={classes.close} aria-label="Close" onClick={onClose}>
            <CloseRoundedIcon aria-hidden style={{ fontSize: 22 }} />
          </button>
        </div>
        <div className={classes.body}>{children}</div>
        {footer}
      </div>
    </Modal>
  );
};

export default Sheet;
